import { Button } from "./ui/button";
import {
  Play,
  Pause,
  Rewind,
  FastForward,
  SkipBack,
  SkipForward,
  Repeat,
  X,
  Headphones,
  Music,
  Zap,
  Moon,
  Flame,
  Coffee,
  Target,
} from "lucide-react";
import { usePlayer } from "../contexts/PlayerContext";

const vibeIconMap = { Zap, Moon, Flame, Coffee, Target, Music };

/**
 * GlobalAudioPlayer — sticky bottom player rendered by Layout so it persists
 * across route changes. Reads everything from PlayerContext.
 */
export default function GlobalAudioPlayer() {
  const {
    playingItem,
    isPlaying,
    progress,
    repeat,
    setRepeat,
    queue,
    togglePlay,
    rewind15,
    forward15,
    seekToPercent,
    closePlayer,
    playNextInQueue,
    playPrevInQueue,
    hasSubscription,
  } = usePlayer();

  if (!playingItem) return null;

  const VibeIcon =
    (playingItem.vibe_icon && vibeIconMap[playingItem.vibe_icon]) || Headphones;
  const hasQueue = queue && queue.length > 0;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 bg-card border-t border-border p-3 z-50 shadow-lg"
      data-testid="global-audio-player"
    >
      <div className="container mx-auto max-w-5xl">
        <div className="flex items-center gap-3 sm:gap-4">
          {/* Track info */}
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 bg-primary/15">
              <VibeIcon className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0">
              <h4 className="font-medium text-sm truncate">{playingItem.title}</h4>
              {playingItem.song_title && (
                <p className="text-xs text-foreground/80 truncate">
                  {playingItem.song_title}
                </p>
              )}
              <p className="text-xs text-muted-foreground truncate">
                {playingItem.course_name}
                {playingItem.vibe ? ` • ${playingItem.vibe}` : ""}
              </p>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-1 sm:gap-2">
            {hasQueue ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={playPrevInQueue}
                title="Previous"
                data-testid="player-prev"
              >
                <SkipBack className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                size="sm"
                variant="ghost"
                className="text-muted-foreground hover:text-foreground hidden sm:inline-flex"
                onClick={rewind15}
                title="Rewind 15s"
                data-testid="player-rewind"
              >
                <Rewind className="h-4 w-4" />
              </Button>
            )}

            <Button
              size="sm"
              className="bg-primary text-primary-foreground hover:bg-primary/90 w-10 h-10 rounded-full"
              onClick={togglePlay}
              data-testid="player-toggle"
            >
              {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
            </Button>

            {hasQueue ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={playNextInQueue}
                title="Next"
                data-testid="player-next"
              >
                <SkipForward className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                size="sm"
                variant="ghost"
                className="text-muted-foreground hover:text-foreground hidden sm:inline-flex"
                onClick={forward15}
                title="Forward 15s"
                data-testid="player-forward"
              >
                <FastForward className="h-4 w-4" />
              </Button>
            )}

            <Button
              size="sm"
              variant="ghost"
              className={`${repeat ? "text-primary" : "text-muted-foreground"} hidden sm:inline-flex`}
              onClick={() => setRepeat(!repeat)}
              title={repeat ? "Repeat on" : "Repeat off"}
              data-testid="player-repeat"
            >
              <Repeat className="h-4 w-4" />
            </Button>
          </div>

          {/* Progress bar */}
          <div className="hidden md:block flex-1 max-w-xs">
            <input
              type="range"
              min="0"
              max="100"
              value={progress}
              onChange={(e) => seekToPercent(parseFloat(e.target.value))}
              className="w-full h-1 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
              data-testid="player-progress"
            />
            {hasSubscription === false && (
              <p className="text-[10px] text-muted-foreground mt-0.5 text-center">
                40-second preview
              </p>
            )}
          </div>

          {/* Close */}
          <Button
            size="sm"
            variant="ghost"
            onClick={closePlayer}
            title="Close player"
            data-testid="player-close"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
